import React, { useEffect, useRef, useState } from 'react';
import {
  Calendar3,
  CashCoin,
  Plus,
  Trash3,
  PlayFill,
  People,
  HourglassSplit,
  CheckCircleFill,
  XCircle,
  ChevronDown,
} from 'react-bootstrap-icons';
import { useLocale } from '../i18n/LocaleContext';
import { fetchBriefs, applyToBrief, submitBriefVideos } from '../api/creators';
import './CreatorTabs.scss';

// Which creator-states let the creator actually upload + submit videos.
const WORKING_STATES = new Set(['confirmed', 'in_review', 'approved']);

// Briefs tab: a list of briefs, each rendering `requiredVideos` upload slots.
// Data comes from GET /creators/briefs (fetchBriefs). Applying hits
// POST /creators/briefs/:id/apply; the video upload hits
// POST /creators/briefs/:id/submit (submitBriefVideos, multipart).
//
// Order of the collapsible groups shown on the Briefs tab. Working states
// (confirmed / in_review / approved) are intentionally EXCLUDED — those live on
// the Tasks tab. So Briefs shows only: open → applied → not_selected.
const BRIEF_GROUPS = ['open', 'applied', 'not_selected'];

export default function BriefsTab() {
  const { t } = useLocale();
  const [allBriefs, setAllBriefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      setAllBriefs(await fetchBriefs());
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="panel-card">
        <div className="panel-card__muted">{t('shell.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="panel-card">
        <div className="panel-card__muted">{t('briefs.loadError')}</div>
        <button className="btn-o btn-o--dark btn-o--sm" onClick={load}>
          {t('shell.retry')}
        </button>
      </div>
    );
  }

  const briefs = allBriefs.filter(
    (b) => !WORKING_STATES.has(b.creatorState || 'open')
  );

  if (!briefs.length) {
    return (
      <div className="panel-card">
        <div className="panel-card__muted">{t('briefs.empty')}</div>
      </div>
    );
  }

  const groups = BRIEF_GROUPS.map((state) => ({
    state,
    items: briefs.filter((b) => (b.creatorState || 'open') === state),
  })).filter((g) => g.items.length > 0);

  return (
    <div className="briefs">
      {groups.map((g, i) => (
        // First two groups (Nyitott, Jelentkeztél) start open; the rest closed.
        <BriefGroup key={g.state} state={g.state} items={g.items} defaultOpen={i < 2} />
      ))}
    </div>
  );
}

// A collapsible group of briefs. The whole body rolls open/closed via a
// grid-template-rows 0fr↔1fr transition (smooth for any content height), and
// each brief card slides up + fades in with a staggered delay when opening.
function BriefGroup({ state, items, defaultOpen }) {
  const { t } = useLocale();
  const [open, setOpen] = useState(!!defaultOpen);
  // Keep the content mounted through the collapse animation, then unmount so it
  // doesn't stay in the tab order while hidden.
  const [mounted, setMounted] = useState(!!defaultOpen);

  function toggle() {
    setOpen((prev) => {
      const next = !prev;
      if (next) setMounted(true);
      return next;
    });
  }

  // After the roll's own collapse animation finishes, unmount the body. Ignore
  // transitions bubbling up from the cards inside.
  function onCollapseEnd(e) {
    if (e.propertyName === 'grid-template-rows' && !open) setMounted(false);
  }

  return (
    <section className={`brief-group ${open ? 'is-open' : ''}`}>
      <button
        type="button"
        className="brief-group__head"
        onClick={toggle}
        aria-expanded={open}
      >
        <span className={`brief-group__dot is-${state}`} />
        <span className="brief-group__title">{t(`briefs.group.${state}`)}</span>
        <span className="brief-group__count">{items.length}</span>
        <ChevronDown size={18} className="brief-group__chev" />
      </button>

      <div
        className={`brief-group__roll ${open ? 'is-open' : ''}`}
        onTransitionEnd={onCollapseEnd}
      >
        <div className="brief-group__roll-inner">
          {mounted && (
            <div className="brief-group__body">
              {items.map((brief, i) => (
                <div
                  className={`brief-group__item ${open ? 'is-in' : ''}`}
                  key={brief.id}
                  style={{ '--i': i }}
                >
                  <BriefCard brief={brief} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function BriefCard({ brief }) {
  const { t } = useLocale();
  const state = brief.creatorState || 'open';
  const isWorking = WORKING_STATES.has(state);

  // Optimistic apply: click "Jelentkezés" → POST /creators/briefs/:id/apply,
  // then flip the state to "applied".
  const [applied, setApplied] = useState(false);
  const effectiveState = applied ? 'applied' : state;

  const total = brief.requiredVideos;
  const requiredLabel =
    total === 1
      ? t('briefs.required', { count: total })
      : t('briefs.required_plural', { count: total });

  const paymentLabel = [
    brief.amount != null &&
      t('briefs.perVideo', {
        amount: `${Number(brief.amount).toLocaleString()} ${(brief.currency || '').toUpperCase()}`.trim(),
      }),
    brief.payment,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className={`brief-card state-${effectiveState}`}>
      <div className="brief-card__head">
        <div className="brief-card__headline">
          {brief.brand && <BrandChip brand={brief.brand} />}
          <h3 className="brief-card__title">{brief.title}</h3>
        </div>
        <span className={`brief-card__status is-${effectiveState}`}>
          {t(`briefs.state.${effectiveState}`)}
        </span>
      </div>

      <BriefDescription text={brief.description} />

      <div className="brief-card__meta">
        {/* Confirmed / working briefs show the DELIVERY deadline; open/applied
            briefs show the APPLICATION deadline. */}
        {isWorking && brief.deadline && (
          <span className="brief-card__meta-item">
            <Calendar3 size={14} />
            {t('briefs.deadline')}: {brief.deadline}
          </span>
        )}
        {!isWorking && brief.applyDeadline && (
          <span className="brief-card__meta-item">
            <Calendar3 size={14} />
            {t('briefs.applyDeadline')}: {brief.applyDeadline}
          </span>
        )}
        {paymentLabel && (
          <span className="brief-card__meta-item">
            <CashCoin size={14} />
            {t('briefs.payment')}: {paymentLabel}
          </span>
        )}
        <span className="brief-card__req">{requiredLabel}</span>
      </div>

      {isWorking ? (
        <WorkArea brief={brief} readOnly={state !== 'confirmed'} />
      ) : (
        <ApplyArea
          brief={brief}
          state={effectiveState}
          onApplied={() => setApplied(true)}
        />
      )}
    </div>
  );
}

// ── Apply area (open / applied / not_selected) ──────────────────────────────
function ApplyArea({ brief, state, onApplied }) {
  const { t } = useLocale();
  const [applying, setApplying] = useState(false);
  const [err, setErr] = useState('');

  async function handleApply() {
    setErr('');
    setApplying(true);
    try {
      await applyToBrief(brief.id);
      if (onApplied) onApplied();
    } catch {
      setErr(t('briefs.applyError'));
    } finally {
      setApplying(false);
    }
  }

  if (state === 'applied') {
    return (
      <div className="brief-status brief-status--applied">
        <HourglassSplit size={18} />
        <div>
          <div className="brief-status__title">{t('briefs.appliedTitle')}</div>
          <div className="brief-status__body">{t('briefs.appliedBody')}</div>
        </div>
      </div>
    );
  }

  if (state === 'not_selected') {
    return (
      <div className="brief-status brief-status--closed">
        <XCircle size={18} />
        <div>
          <div className="brief-status__title">{t('briefs.notSelectedTitle')}</div>
          <div className="brief-status__body">{t('briefs.notSelectedBody')}</div>
        </div>
      </div>
    );
  }

  // OPEN — can still apply.
  return (
    <div className="brief-apply">
      {(brief.slotsNeeded != null || brief.applicantCount != null) && (
        <div className="brief-apply__stats">
          <People size={15} />
          {/* How many spots are already taken, not just how many people are
              waiting: "5 applicants" says nothing about whether it is full.
              Falls back to the plain target when the API predates
              participantCount. */}
          {brief.slotsNeeded != null && (
            <span>
              {brief.participantCount != null
                ? t('briefs.spotsTaken', {
                    taken: brief.participantCount,
                    total: brief.slotsNeeded,
                  })
                : t('briefs.slotsNeeded', { count: brief.slotsNeeded })}
            </span>
          )}
          {brief.applicantCount != null && (
            <>
              <span className="brief-apply__dot">·</span>
              <span>{t('briefs.applicants', { count: brief.applicantCount })}</span>
            </>
          )}
        </div>
      )}
      <button
        className="btn-o btn-o--dark btn-o--sm"
        onClick={handleApply}
        disabled={applying}
      >
        {applying ? t('apply.submitting') : t('briefs.apply')}
      </button>
      {err && <div className="brief-card__notice">{err}</div>}
      <p className="brief-apply__note">
        {t('briefs.applyOpenNote', { date: brief.applyDeadline })}
      </p>
    </div>
  );
}

// ── Work area (confirmed → upload + submit; in_review/approved read-only) ────
function WorkArea({ brief, readOnly }) {
  const { t } = useLocale();
  const [slots, setSlots] = useState(() => Array(brief.requiredVideos).fill(null));
  const [notice, setNotice] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    return () => {
      slots.forEach((s) => s?.url && URL.revokeObjectURL(s.url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function setSlot(index, value) {
    setSlots((prev) => {
      const next = [...prev];
      if (prev[index]?.url) URL.revokeObjectURL(prev[index].url);
      next[index] = value;
      return next;
    });
    setNotice('');
  }

  function onPick(index, file) {
    if (!file) return;
    if (!file.type.startsWith('video/')) return;
    setSlot(index, { file, url: URL.createObjectURL(file) });
  }

  function onRemove(index) {
    setSlot(index, null);
  }

  const done = slots.filter(Boolean).length;
  const total = brief.requiredVideos;
  const allUploaded = done === total;

  const [submitted, setSubmitted] = useState(false);

  async function handleSubmit() {
    if (!allUploaded) {
      setNotice(t('briefs.submitAllFirst', { total }));
      return;
    }
    setSubmitting(true);
    try {
      const files = slots.map((s) => s.file);
      const slotIndexes = slots.map((_, i) => i);
      await submitBriefVideos(brief.id, files, slotIndexes);
      setSubmitted(true);
      setNotice(t('briefs.submitted'));
    } catch {
      setNotice(t('briefs.submitError'));
    } finally {
      setSubmitting(false);
    }
  }

  // Confirmed briefs get a short "you're in, start creating" banner.
  return (
    <>
      {brief.creatorState === 'confirmed' && (
        <div className="brief-status brief-status--confirmed">
          <CheckCircleFill size={18} />
          <div>
            <div className="brief-status__title">{t('briefs.confirmedTitle')}</div>
            <div className="brief-status__body">{t('briefs.confirmedBody')}</div>
          </div>
        </div>
      )}

      <div className="brief-card__slots-head">
        <span>{t('briefs.uploaded', { done, total })}</span>
      </div>

      <div className="slot-grid">
        {slots.map((slot, i) => (
          <VideoSlot
            key={i}
            slot={slot}
            readOnly={readOnly}
            onPick={(file) => onPick(i, file)}
            onRemove={() => onRemove(i)}
          />
        ))}
      </div>

      {!readOnly && (
        <div className="brief-card__foot">
          <button
            className="btn-o btn-o--dark btn-o--sm"
            onClick={handleSubmit}
            disabled={submitting || submitted}
          >
            {submitting ? t('briefs.submitting') : t('briefs.submit')}
          </button>
          {notice && <span className="brief-card__notice">{notice}</span>}
        </div>
      )}
    </>
  );
}

// Who posted the brief — just a small logo + name, no label. Falls back to a
// coloured initial tile when there's no logo URL.
function BrandChip({ brand }) {
  return (
    <span className="brand-chip">
      {brand.logo ? (
        <img className="brand-chip__logo" src={brand.logo} alt="" />
      ) : (
        <span className="brand-chip__logo brand-chip__logo--ph">
          {brand.name?.[0]?.toUpperCase() || 'B'}
        </span>
      )}
      <span className="brand-chip__name">{brand.name}</span>
    </span>
  );
}

// Brief description: clamped to 5 lines, with a "Tovább" / "Kevesebb" toggle
// that only appears when the text actually overflows. Preserves paragraph
// breaks (the mock text uses \n\n).
function BriefDescription({ text }) {
  const { t } = useLocale();
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const ref = useRef(null);

  // Detect overflow while collapsed (compare scroll height to clamped height).
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!expanded) {
      setOverflows(el.scrollHeight - el.clientHeight > 2);
    }
  }, [expanded, text]);

  return (
    <div className="brief-card__desc-wrap">
      <p
        ref={ref}
        className={`brief-card__desc ${expanded ? 'is-expanded' : 'is-clamped'}`}
      >
        {text}
      </p>
      {(overflows || expanded) && (
        <button
          type="button"
          className="brief-card__more"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? t('briefs.less') : t('briefs.more')}
        </button>
      )}
    </div>
  );
}

function VideoSlot({ slot, onPick, onRemove, readOnly }) {
  const { t } = useLocale();
  const inputRef = useRef(null);

  if (slot) {
    return (
      <div className="slot slot--filled">
        <video className="slot__video" src={slot.url} muted playsInline preload="metadata" />
        <span className="slot__play" aria-hidden="true">
          <PlayFill size={22} />
        </span>
        {!readOnly && (
          <button
            type="button"
            className="slot__remove"
            onClick={onRemove}
            title={t('briefs.remove')}
            aria-label={t('briefs.remove')}
          >
            <Trash3 size={15} />
          </button>
        )}
        <span className="slot__name" title={slot.file.name}>
          {slot.file.name}
        </span>
      </div>
    );
  }

  // Read-only empty slot (in_review / approved): just a muted placeholder.
  if (readOnly) {
    return (
      <div className="slot slot--empty slot--readonly" aria-disabled="true">
        <span className="slot__plus">
          <PlayFill size={22} />
        </span>
      </div>
    );
  }

  return (
    <button
      type="button"
      className="slot slot--empty"
      onClick={() => inputRef.current?.click()}
    >
      <span className="slot__plus">
        <Plus size={26} />
      </span>
      <span className="slot__hint">{t('briefs.slotHint')}</span>
      <span className="slot__any">{t('briefs.slotAny')}</span>
      <input
        ref={inputRef}
        type="file"
        accept="video/*"
        hidden
        onChange={(e) => {
          onPick(e.target.files?.[0]);
          e.target.value = ''; // allow re-picking the same file later
        }}
      />
    </button>
  );
}
